import React, { useEffect, useRef, useState } from "react";
import ApexCharts from "apexcharts";
import customerItemService from "../../../../services/customer_appCommonService";
import { PERIODS, rangeFor } from "./dashboardPeriod";
import { attachPdfMenu } from "../../Charts/chartPdf";
import { usePanelMotion } from "./DashboardSection";

function Chart({ type, data, height, exportName }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new ApexCharts(ref.current, {
      chart: {
        type,
        height,
        zoom: { enabled: false },
        redrawOnParentResize: false,
        redrawOnWindowResize: false,
        animations: { enabled: false },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
      },
      series: data?.series?.length ? data.series : [{ name: "Quantity ordered", data: [] }],
      xaxis: { categories: data?.categories || [] },
      stroke: { curve: "straight", width: type === "line" ? 3 : 0 },
      colors: ["#008FFB"],
      plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
      dataLabels: {
        enabled: type === "bar",
        offsetY: -6,
        style: { fontSize: "12px", colors: ["#14233d"], fontWeight: 600 },
      },
      yaxis: { title: { text: "Quantity ordered" }, decimalsInFloat: 0 },
      grid: { borderColor: "#e8eaf6", strokeDashArray: 4 },
      tooltip: {
        theme: "light",
        fillSeriesColor: false,
        y: {
          formatter: (val) => (val == null || Number.isNaN(Number(val)) ? "" : String(Number(val))),
        },
      },
      noData: { text: "No orders in this date range" },
    });
    const root = ref.current;
    chart.render().then(() => attachPdfMenu(chart, root, exportName));
    return () => chart.destroy();
  }, [type, data, height, exportName]);
  return <div ref={ref} />;
}

export default function FastMovingMaterials({ officers, statuses, lockedOfficer }) {
  const initial = rangeFor("today");
  const [period, setPeriod] = useState("today");
  const [fromDate, setFromDate] = useState(initial.fromDate);
  const [toDate, setToDate] = useState(initial.toDate);
  const [material, setMaterial] = useState("");
  const [salesOfficerCode, setSalesOfficerCode] = useState(lockedOfficer || "");
  const [salesOrderStatus, setSalesOrderStatus] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const panelRef = useRef(null);
  usePanelMotion(open, panelRef);

  useEffect(() => {
    if (lockedOfficer) setSalesOfficerCode(lockedOfficer);
  }, [lockedOfficer]);

  const applyPeriod = (next) => {
    setPeriod(next);
    if (next === "custom") return;
    const span = rangeFor(next);
    setFromDate(span.fromDate);
    setToDate(span.toDate);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    customerItemService.dashboardFastMoving({
      period,
      fromDate,
      toDate: toDate || fromDate,
      material,
      salesOfficerCode: lockedOfficer || salesOfficerCode,
      salesOrderStatus,
    }).then((result) => {
      if (!cancelled) setReport(result?.status === 200 ? result.data : { rows: [], bars: {}, trend: {} });
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [period, fromDate, toDate, material, salesOfficerCode, salesOrderStatus, lockedOfficer]);

  const rows = report?.rows || [];

  return (
    <div className="col-lg-12 commTopButtonRightLeftPadding">
      <div className={`card dash-chart-card ${open ? "is-open" : ""}`}>
        <button type="button" className="dash-fold-head dash-chart-head" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          <h5 className="card-title mb-0">Fast-Moving Materials</h5>
          <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden="true" />
        </button>
        <div className="dash-fold-panel" ref={panelRef}>
        <div className="dash-fold-panel-inner">
        <div className="card-body dash-chart-body">
          <div className="dash-filter-grid dash-filter-grid-6">
            <div className="dash-field">
              <label className="form-label">Period</label>
              <select className="form-select" value={period} onChange={(event) => applyPeriod(event.target.value)}>
                {PERIODS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select>
            </div>
            <div className="dash-field">
              <label className="form-label">From date</label>
              <input className="form-control" type="date" value={fromDate} disabled={period !== "custom"} onChange={(event) => { setPeriod("custom"); setFromDate(event.target.value); }} />
            </div>
            <div className="dash-field">
              <label className="form-label">To date</label>
              <input className="form-control" type="date" value={toDate} disabled={period !== "custom"} onChange={(event) => { setPeriod("custom"); setToDate(event.target.value); }} />
            </div>
            <div className="dash-field">
              <label className="form-label">Material name / code</label>
              <input className="form-control" value={material} placeholder="All materials" onChange={(event) => setMaterial(event.target.value)} />
            </div>
            <div className="dash-field">
              <label className="form-label">Sales Officer Code</label>
              <select className="form-select" value={lockedOfficer || salesOfficerCode} disabled={Boolean(lockedOfficer)} onChange={(event) => setSalesOfficerCode(event.target.value)}>
                {!lockedOfficer && <option value="">All officers</option>}
                {officers.map((officer) => (
                  <option key={officer.code} value={officer.code}>{officer.code}{officer.name ? ` - ${officer.name}` : ""}</option>
                ))}
              </select>
            </div>
            <div className="dash-field">
              <label className="form-label">Sales Order Status</label>
              <select className="form-select" value={salesOrderStatus} onChange={(event) => setSalesOrderStatus(event.target.value)}>
                <option value="">All statuses</option>
                {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
              </select>
            </div>
          </div>
          {loading && <p className="text-muted">Loading materials...</p>}
          <div className="row">
            <div className="col-lg-6">
              <h6>Quantity ordered by material</h6>
              <Chart type="bar" data={report?.bars} height={320} exportName="fast-moving-quantity" />
            </div>
            <div className="col-lg-6">
              <h6>Order quantity trend</h6>
              <Chart type="line" data={report?.trend} height={320} exportName="fast-moving-trend" />
            </div>
          </div>
          <div className="table-responsive mt-3">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Material code</th>
                  <th>Material name</th>
                  <th className="text-end">Total quantity ordered</th>
                  <th className="text-end">Current stock</th>
                  <th className="text-end">Order count</th>
                  <th>Movement</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.materialCode}-${row.materialName}`}>
                    <td>{row.materialCode || "—"}</td>
                    <td>{row.materialName}</td>
                    <td className="text-end">{row.totalQuantity}</td>
                    <td className="text-end">{row.currentStock}</td>
                    <td className="text-end">{row.salesCount}</td>
                    <td>{row.movement}</td>
                  </tr>
                ))}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={6}>No orders in this date range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}
